import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Download, ArrowUpRight } from 'lucide-react';
import { navigationLinks, personalInfo } from '../../data/mock';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    const index = navigationLinks.findIndex(link => link.path === location.pathname);
    setActiveIndex(index >= 0 ? index : 0);
  }, [location]);

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'py-3'
            : 'py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className={`flex items-center justify-between rounded-full px-6 py-3 transition-all duration-500 ${
            isScrolled ? 'bg-white/5 backdrop-blur-xl border border-white/10' : ''
          }`}>
            {/* Logo */}
            <Link
              to="/"
              className="group flex items-center gap-2"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00ff88] to-[#00cc6a] flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300">
                <span className="text-black font-bold text-lg">A</span>
              </div>
              <span className="text-white font-semibold text-lg hidden sm:block">
                {personalInfo.name.split(' ')[0]}
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 bg-white/5 rounded-full p-1.5">
              {navigationLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-5 py-2.5 text-sm font-medium transition-all duration-300 rounded-full ${
                    location.pathname === link.path
                      ? 'text-black'
                      : 'text-white/70 hover:text-white'
                  } ${link.isCV ? 'flex items-center gap-1.5' : ''}`}
                >
                  {location.pathname === link.path && (
                    <span 
                      className="absolute inset-0 bg-[#00ff88] rounded-full -z-10"
                      style={{
                        animation: 'scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                      }}
                    />
                  )}
                  {link.isCV && <Download size={14} />}
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:block">
              <Link to="/contact">
                <button className="group relative px-6 py-3 bg-transparent border border-[#00ff88] text-[#00ff88] rounded-full font-medium overflow-hidden transition-all duration-300 hover:text-black">
                  <span className="relative z-10 flex items-center gap-2">
                    Let's Talk
                    <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                  <span className="absolute inset-0 bg-[#00ff88] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              <div className="relative w-5 h-4">
                <span className={`absolute left-0 w-full h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'}`} />
                <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 scale-0' : ''}`} />
                <span className={`absolute left-0 w-full h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0'}`} />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Full Screen */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" />
        <div className="relative h-full flex flex-col justify-center items-center p-8">
          <div className="space-y-2 text-center">
            {navigationLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className="block overflow-hidden"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <span 
                  className={`block text-4xl sm:text-5xl font-bold transition-all duration-500 ${
                    isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                  } ${
                    location.pathname === link.path ? 'text-[#00ff88]' : 'text-white hover:text-[#00ff88]'
                  }`}
                  style={{ transitionDelay: `${index * 80 + 200}ms` }}
                >
                  {link.label}
                </span>
              </Link>
            ))}
          </div>
          
          <Link 
            to="/contact" 
            className={`mt-12 transition-all duration-500 ${isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            style={{ transitionDelay: '600ms' }}
          >
            <button className="px-10 py-4 bg-[#00ff88] text-black rounded-full font-semibold text-lg hover:bg-[#00cc6a] transition-colors">
              Start a Project
            </button>
          </Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes scaleIn {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export default Navbar;
